import React, { useCallback, useEffect } from 'react'
import { useReactiveVar } from '@apollo/client'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { ProjectCreateStepper } from 'modules/ProjectCreate/components'
import { labelCodeIdVar, notificationMessageVar } from 'modulesCommon/store'
import { useLoading, usePrevious, useServerError } from 'shared/hooks'
import { useGpsUnits } from 'modulesCommon/serviceHooks'
import { productSchema } from '../constants'
import { IProjectForm } from '../types'
import { useZoneTemplates } from 'modules/Labels/serviceHooks/useGetZoneTemplates'
import { useSearchArtistByName } from '../serviceHooks/useSearchArtist'

import { IArtist } from 'shared/interfaces'
import { LabelTemplateType } from 'shared/__gql-types'

const ProjectCreate = () => {
  const zoneId = useReactiveVar(labelCodeIdVar)
  const prevZoneId = usePrevious(zoneId)
  const { gpsUnitsLoading, gpsUnitsError } = useGpsUnits()
  const { getArtistByName } = useSearchArtistByName()
  const { getTemplateByType, loading: templateLoading, zoneTemplatesResponse } = useZoneTemplates(zoneId)

  const checkArtistName = useCallback<(name: string) => Promise<IArtist | undefined>>(
    async (name) => {
      const data = await getArtistByName(name)
      return data.find((artist: IArtist) => artist.name.toLowerCase() === name.toLowerCase())
    },
  [getArtistByName]
  )

  const methods = useForm<IProjectForm>({
    defaultValues: {
      artistId: '',
      artistName: '',
      searchedName: '',
      firstCreatedIn: zoneId,
      labelId: zoneId,
      gpsStatus: '',
      opusId: '',
      title: '',
      url: '',
      facebookId: '',
      spotifyId: '',
      projectName: '',
      unit: '',
      isaacUrl: [],
      teamInfo: '',
      info: '',
      products: [],
      artistProducts: [],
      step: 0
    },
    resolver: yupResolver(productSchema({ checkArtistName }))
  })

  useEffect(() => {
    if (zoneTemplatesResponse) {
      methods.reset({
        ...methods.getValues(),
        teamInfo: getTemplateByType(LabelTemplateType.TeamInfo),
        info: getTemplateByType(LabelTemplateType.CampaignInfo)
      })
    }
  }, [zoneTemplatesResponse, getTemplateByType, methods])

  useEffect(() => {
    methods.reset({ ...methods.getValues(), firstCreatedIn: zoneId, labelId: zoneId })
  }, [methods, zoneId])

  useEffect(() => {
    const step = methods.getValues('step')
    if (zoneId !== prevZoneId && step > 0) {
      notificationMessageVar({
        type: 'warning',
        message: 'You changed your zone. Please, reselect unit',
        title: 'Warning'
      })
      methods.reset({ ...methods.getValues(), firstCreatedIn: zoneId, labelId: zoneId, step: 1, unit: '' })
    }
  }, [zoneId, prevZoneId, methods])

  useLoading({ isLoading: gpsUnitsLoading || templateLoading })
  useServerError([gpsUnitsError])

  return (
    <div className="project-create">
      <FormProvider {...methods}>
        <form>
          <ProjectCreateStepper />
        </form>
      </FormProvider>
    </div>
  )
}

export default ProjectCreate
